import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { ApiError } from '@/deploy/services/api';
import {
  mongoService,
  type MongoDatabase,
  type MongoCollectionInfo,
  type MongoCopyResult,
} from '@/deploy/services/mongo.service';
import { useSessionStore } from '@/deploy/stores/session.store';
import type { Project } from '@/deploy/types';
import { apiGroupLabel } from '@/deploy/utils/apiEnvs';
import styles from './MongoExplorer.module.scss';

function errMsg(e: unknown): string {
  return e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e);
}

/** Portée d'une copie : collections cochées / base entière / tout l'environnement. */
type CopyJob = { mode: 'collections' | 'database' | 'environment'; db?: string; colls?: string[] };

// Environnements ACCEPTÉS EN CIBLE (prod exclu : lecture seule, bloqué 403 côté backend).
// Dev et Bêta partagent les serveurs API, donc le même MongoDB : une seule cible « Dev + Bêta ».
const WRITABLE_ENVS = ['dev', 'preprod'] as const;

/** Même MongoDB derrière les deux envs (dev et beta sont un seul groupe). */
const sameMongoEnv = (a: string, b: string) => apiGroupLabel(a) === apiGroupLabel(b);

/**
 * Explorateur Mongo (type Compass) scopé à un environnement.
 * Chargement paresseux : rien n'est requêté tant que le panneau n'est pas ouvert.
 * En prod → lecture seule (aucune action d'écriture proposée).
 */
export function MongoExplorer({ projectId, env, defaultOpen }: { projectId: string; env: string; defaultOpen?: boolean }) {
  const readOnly = env === 'prod';
  const [open, setOpen] = useState(defaultOpen ?? false);

  const [databases, setDatabases] = useState<MongoDatabase[] | null>(null);
  const [dbLoading, setDbLoading] = useState(false);
  const [dbError, setDbError] = useState<string | null>(null);

  const [expandedDb, setExpandedDb] = useState<string | null>(null);
  const [collsByDb, setCollsByDb] = useState<Record<string, MongoCollectionInfo[]>>({});
  const [collLoading, setCollLoading] = useState<string | null>(null);
  const [collError, setCollError] = useState<{ db: string; msg: string } | null>(null);

  // Jeton de "session de chargement" : bumpé à chaque reset (ouverture / changement d'env).
  // Toute réponse asynchrone d'une session précédente est ignorée (évite qu'une réponse
  // en vol d'un AUTRE env vienne polluer l'arbre après le reset).
  const loadSeq = useRef(0);
  // Jeton de requête find : seule la dernière requête applique son résultat (évite qu'un
  // find lent d'une collection A écrase les docs d'une collection B sélectionnée après).
  const findReq = useRef(0);

  const [selected, setSelected] = useState<{ db: string; coll: string } | null>(null);
  const [filterText, setFilterText] = useState('{}');
  const [limit, setLimit] = useState(50);
  const [skip, setSkip] = useState(0);
  const [docs, setDocs] = useState<Record<string, unknown>[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);

  const [insertOpen, setInsertOpen] = useState(false);
  const [insertText, setInsertText] = useState('{\n  \n}');
  const [busy, setBusy] = useState(false);

  // Copie de structure/données vers un autre env/projet.
  const projects = useSessionStore((s) => s.projects);
  const [copySel, setCopySel] = useState<{ db: string; colls: string[] } | null>(null); // collections cochées
  const [copyJob, setCopyJob] = useState<CopyJob | null>(null); // ouvre la modale de copie

  const loadDatabases = useCallback(() => {
    const seq = loadSeq.current;
    setDbLoading(true);
    setDbError(null);
    mongoService.databases(projectId, env)
      .then((d) => { if (loadSeq.current === seq) setDatabases(d); })
      .catch((e) => { if (loadSeq.current === seq) setDbError(errMsg(e)); })
      .finally(() => { if (loadSeq.current === seq) setDbLoading(false); });
  }, [projectId, env]);

  // (Re)initialise tout à l'ouverture / au changement d'env.
  useEffect(() => {
    if (!open) return;
    // Invalide toutes les réponses en vol de la session précédente (env précédent).
    loadSeq.current += 1;
    findReq.current += 1;
    setExpandedDb(null);
    setCollsByDb({});
    setCollError(null);
    setSelected(null);
    setDocs(null);
    setTotal(null);
    setDocsError(null);
    setInsertOpen(false);
    setCopySel(null);
    setCopyJob(null);
    loadDatabases();
  }, [open, loadDatabases]);

  const toggleDb = (name: string) => {
    if (expandedDb === name) { setExpandedDb(null); return; }
    setExpandedDb(name);
    if (collError?.db === name) setCollError(null);
    if (!collsByDb[name]) {
      const seq = loadSeq.current;
      setCollLoading(name);
      mongoService.collections(projectId, env, name)
        .then((c) => { if (loadSeq.current === seq) setCollsByDb((m) => ({ ...m, [name]: c })); })
        .catch((e) => { if (loadSeq.current === seq) setCollError({ db: name, msg: errMsg(e) }); })
        .finally(() => { if (loadSeq.current === seq) setCollLoading((c) => (c === name ? null : c)); });
    }
  };

  // Coche/décoche une collection pour la copie. La sélection est liée à UNE base source :
  // cocher dans une autre base réinitialise la sélection (une copie = une base source).
  const toggleCopy = (db: string, coll: string) => {
    setCopySel((s) => {
      if (!s || s.db !== db) return { db, colls: [coll] };
      const has = s.colls.includes(coll);
      const colls = has ? s.colls.filter((c) => c !== coll) : [...s.colls, coll];
      return colls.length ? { db, colls } : null;
    });
  };

  const runFind = useCallback((db: string, coll: string, nextSkip: number) => {
    let filter: unknown;
    try { filter = filterText.trim() ? JSON.parse(filterText) : {}; }
    catch { setDocsError('Filtre JSON invalide.'); return; }
    const req = ++findReq.current;
    setDocsLoading(true);
    setDocsError(null);
    mongoService.find(projectId, env, db, coll, { filter, limit, skip: nextSkip })
      .then((r) => { if (findReq.current !== req) return; setDocs(r.documents); setTotal(r.total ?? null); setSkip(nextSkip); })
      .catch((e) => { if (findReq.current !== req) return; setDocs(null); setDocsError(errMsg(e)); })
      .finally(() => { if (findReq.current === req) setDocsLoading(false); });
  }, [projectId, env, filterText, limit]);

  const selectColl = (db: string, coll: string) => {
    setSelected({ db, coll });
    setInsertOpen(false);
    runFind(db, coll, 0);
  };

  const refresh = () => { if (selected) runFind(selected.db, selected.coll, skip); };

  const doInsert = async () => {
    if (!selected) return;
    let doc: unknown;
    try { doc = JSON.parse(insertText); }
    catch { alert('Document JSON invalide.'); return; }
    setBusy(true);
    try {
      await mongoService.insertOne(projectId, env, selected.db, selected.coll, doc);
      setInsertOpen(false);
      setInsertText('{\n  \n}');
      runFind(selected.db, selected.coll, skip);
    } catch (e) { alert('Insert KO : ' + errMsg(e)); }
    finally { setBusy(false); }
  };

  const total0 = total ?? 0;
  const canPrev = skip > 0;
  const canNext = docs != null && docs.length === limit && (total == null || skip + limit < total0);

  return (
    <section className={styles.panel}>
      <button type="button" className={styles.head} onClick={() => setOpen((o) => !o)}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
        <Icon name="layers" size={16} />
        <h2>Base de données · MongoDB</h2>
        <Badge tone="muted">{env}</Badge>
        {readOnly && <Badge tone="warning">lecture seule</Badge>}
        <span style={{ flex: 1 }} />
        {open && databases && databases.length > 0 && (
          <span
            role="button"
            tabIndex={0}
            className={styles.exportAllBtn}
            onClick={(e) => { e.stopPropagation(); setCopyJob({ mode: 'environment' }); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setCopyJob({ mode: 'environment' }); } }}
            title="Exporter tout l'environnement vers un autre projet/env"
          >
            <Icon name="deploy" size={12} /> tout l'env
          </span>
        )}
        {open && (
          <span
            role="button"
            tabIndex={0}
            className={styles.headBtn}
            onClick={(e) => { e.stopPropagation(); loadDatabases(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); loadDatabases(); } }}
            title="Recharger les bases"
          >
            <Icon name="refresh" size={12} />
          </span>
        )}
      </button>

      {open && copySel && (
        <div className={styles.copyBar}>
          <Icon name="deploy" size={13} />
          <span className={styles.copyBarText}>{copySel.colls.length} collection(s) sélectionnée(s) · {copySel.db}</span>
          <span style={{ flex: 1 }} />
          <Button variant="primary" size="sm" onClick={() => setCopyJob({ mode: 'collections', db: copySel.db, colls: copySel.colls })}>
            Copier vers…
          </Button>
          <button className={styles.iconBtn} onClick={() => setCopySel(null)} title="Effacer la sélection">
            <Icon name="x" size={12} />
          </button>
        </div>
      )}

      {open && (
        <div className={styles.body}>
          {/* Colonne gauche : arbre bases → collections */}
          <aside className={styles.tree}>
            {dbLoading && <div className={styles.muted}>Chargement des bases…</div>}
            {dbError && (
              <div className={styles.err}>
                <Icon name="warn" size={12} /> {dbError}
                <div className={styles.errHint}>
                  L'explorateur nécessite le backend Mongo côté recube-cicd (endpoints <code>/mongo</code>). Pas encore disponible ?
                </div>
              </div>
            )}
            {databases && databases.length === 0 && !dbError && <div className={styles.muted}>Aucune base.</div>}
            {databases?.map((db) => {
              const isOpen = expandedDb === db.name;
              const colls = collsByDb[db.name];
              return (
                <div key={db.name}>
                  <div className={styles.dbRow} onClick={() => toggleDb(db.name)}>
                    <Icon name={isOpen ? 'chevron-down' : 'chevron-right'} size={11} />
                    <Icon name="package" size={11} />
                    <span className={styles.name}>{db.name}</span>
                    {typeof db.collections === 'number' && <span className={styles.count}>{db.collections}</span>}
                    <button
                      className={styles.rowBtn}
                      title="Copier cette base entière vers…"
                      onClick={(e) => { e.stopPropagation(); setCopyJob({ mode: 'database', db: db.name }); }}
                    >
                      <Icon name="deploy" size={11} />
                    </button>
                  </div>
                  {isOpen && (
                    <div className={styles.colls}>
                      {collLoading === db.name && <div className={styles.mutedSm}>Chargement…</div>}
                      {collError?.db === db.name && (
                        <div className={styles.mutedSm} style={{ color: 'var(--color-danger)' }}>
                          <Icon name="warn" size={10} /> {collError.msg}
                        </div>
                      )}
                      {colls && colls.length === 0 && collLoading !== db.name && collError?.db !== db.name && <div className={styles.mutedSm}>(vide)</div>}
                      {colls?.map((c) => (
                        <div
                          key={c.name}
                          className={[styles.collRow, selected?.db === db.name && selected?.coll === c.name && styles.collRowActive].filter(Boolean).join(' ')}
                          onClick={() => selectColl(db.name, c.name)}
                        >
                          <input
                            type="checkbox"
                            className={styles.collCheck}
                            checked={copySel?.db === db.name && copySel.colls.includes(c.name)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleCopy(db.name, c.name)}
                            title="Sélectionner pour copie"
                          />
                          <Icon name="git-commit" size={10} />
                          <span className={styles.name}>{c.name}</span>
                          {typeof c.count === 'number' && <span className={styles.count}>{c.count}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </aside>

          {/* Colonne droite : requête + documents */}
          <div className={styles.main}>
            {!selected ? (
              <div className={styles.placeholder}>
                <Icon name="layers" size={20} />
                <span>Sélectionne une collection à gauche pour explorer ses documents.</span>
              </div>
            ) : (
              <>
                <div className={styles.queryBar}>
                  <code className={styles.crumb}>{selected.db} · {selected.coll}</code>
                  <label className={styles.filterField}>
                    <span>filtre</span>
                    <input
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      spellCheck={false}
                      placeholder='{ "champ": "valeur" }'
                    />
                  </label>
                  <label className={styles.limitField}>
                    <span>limit</span>
                    <input
                      type="number"
                      min={1}
                      max={500}
                      value={limit}
                      onChange={(e) => setLimit(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
                    />
                  </label>
                  <Button variant="primary" size="sm" leftIcon={<Icon name="search" size={12} />}
                    loading={docsLoading} onClick={() => runFind(selected.db, selected.coll, 0)}>
                    Exécuter
                  </Button>
                  {!readOnly && (
                    <Button variant="secondary" size="sm" leftIcon={<Icon name="plus" size={12} />}
                      onClick={() => setInsertOpen((o) => !o)}>
                      Insérer
                    </Button>
                  )}
                </div>

                {insertOpen && !readOnly && (
                  <div className={styles.insertBox}>
                    <textarea value={insertText} onChange={(e) => setInsertText(e.target.value)} spellCheck={false} rows={5} />
                    <div className={styles.insertActions}>
                      <Button variant="ghost" size="sm" onClick={() => setInsertOpen(false)}>Annuler</Button>
                      <Button variant="primary" size="sm" loading={busy} onClick={() => void doInsert()}>Insérer le document</Button>
                    </div>
                  </div>
                )}

                {docsError && <div className={styles.err}><Icon name="warn" size={12} /> {docsError}</div>}

                <div className={styles.docs}>
                  {docsLoading && <div className={styles.muted}>Chargement des documents…</div>}
                  {!docsLoading && docs && docs.length === 0 && <div className={styles.muted}>Aucun document.</div>}
                  {!docsLoading && docs?.map((doc, i) => (
                    <DocCard
                      key={(doc._id != null ? JSON.stringify(doc._id) : '') + i}
                      doc={doc}
                      readOnly={readOnly}
                      onSaved={refresh}
                      onDeleted={refresh}
                      projectId={projectId}
                      env={env}
                      db={selected.db}
                      coll={selected.coll}
                    />
                  ))}
                </div>

                {docs && (docs.length > 0 || skip > 0) && (
                  <div className={styles.pager}>
                    <Button variant="ghost" size="sm" disabled={!canPrev || docsLoading}
                      onClick={() => runFind(selected.db, selected.coll, Math.max(0, skip - limit))}>
                      ‹ Précédent
                    </Button>
                    <span className={styles.mutedSm}>
                      {skip + 1}–{skip + (docs.length)}{total != null ? ` / ${total0}` : ''}
                    </span>
                    <Button variant="ghost" size="sm" disabled={!canNext || docsLoading}
                      onClick={() => runFind(selected.db, selected.coll, skip + limit)}>
                      Suivant ›
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {copyJob && (
        <CopyModal
          sourceProjectId={projectId}
          sourceEnv={env}
          projects={projects}
          job={copyJob}
          onClose={() => setCopyJob(null)}
        />
      )}
    </section>
  );
}

/**
 * Modale de copie : source figée (env + portée), cible configurable (projet, env non-prod, base),
 * toggle données. Lance mongoService.copy et affiche le rapport par collection.
 */
function CopyModal({
  sourceProjectId, sourceEnv, projects, job, onClose,
}: {
  sourceProjectId: string;
  sourceEnv: string;
  projects: Project[];
  job: CopyJob;
  onClose: () => void;
}) {
  const [targetProject, setTargetProject] = useState(sourceProjectId);
  const [targetEnv, setTargetEnv] = useState<string>(
    WRITABLE_ENVS.find((e) => !sameMongoEnv(e, sourceEnv)) ?? 'dev',
  );
  const [targetDb, setTargetDb] = useState(job.db ?? '');
  const [withData, setWithData] = useState(false);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<MongoCopyResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const effTargetDb = job.mode === 'environment' ? '' : (targetDb.trim() || (job.db ?? ''));
  const sameTarget =
    targetProject === sourceProjectId && sameMongoEnv(targetEnv, sourceEnv) &&
    (job.mode === 'environment' || effTargetDb === job.db);

  const scopeLabel =
    job.mode === 'environment' ? `toutes les bases (hors système)`
    : job.mode === 'database' ? `base « ${job.db} » (toutes les collections)`
    : `${job.colls?.length ?? 0} collection(s) de « ${job.db} »`;

  const run = async () => {
    setRunning(true); setError(null); setResults(null);
    try {
      const res = await mongoService.copy(sourceProjectId, {
        sourceEnv,
        targetProject,
        targetEnv,
        mode: job.mode,
        sourceDb: job.db,
        targetDb: job.mode === 'environment' ? undefined : effTargetDb,
        collections: job.mode === 'collections' ? job.colls : undefined,
        withData,
      });
      setResults(res.results);
    } catch (e) { setError(errMsg(e)); }
    finally { setRunning(false); }
  };

  const counts = results && {
    copied: results.filter((r) => r.status === 'copied').length,
    skipped: results.filter((r) => r.status === 'skipped').length,
    missing: results.filter((r) => r.status === 'source-missing').length,
    error: results.filter((r) => r.status === 'error').length,
  };

  return (
    <div className={styles.modalOverlay} onClick={running ? undefined : onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHead}>
          <Icon name="deploy" size={16} />
          <h3>Copier vers un autre environnement</h3>
          <span style={{ flex: 1 }} />
          <button className={styles.iconBtn} onClick={onClose} disabled={running} title={running ? 'Copie en cours…' : 'Fermer'}><Icon name="x" size={14} /></button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.field}>
            <span>Source</span>
            <code className={styles.srcCode}>{sourceEnv} · {scopeLabel}</code>
          </div>

          <div className={styles.grid2}>
            <label className={styles.field}>
              <span>Projet cible</span>
              <select value={targetProject} onChange={(e) => setTargetProject(e.target.value)} disabled={!!results}>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ''}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Env cible</span>
              <select value={targetEnv} onChange={(e) => setTargetEnv(e.target.value)} disabled={!!results}>
                {WRITABLE_ENVS.map((e) => <option key={e} value={e}>{apiGroupLabel(e)}</option>)}
              </select>
            </label>
          </div>

          {job.mode !== 'environment' && (
            <label className={styles.field}>
              <span>Base cible</span>
              <input value={targetDb} onChange={(e) => setTargetDb(e.target.value)} placeholder={job.db} spellCheck={false} disabled={!!results} />
            </label>
          )}

          <label className={styles.checkRow}>
            <input type="checkbox" checked={withData} onChange={(e) => setWithData(e.target.checked)} disabled={!!results} />
            <span>Copier aussi les <strong>données</strong> (sinon structure seule : collection + index)</span>
          </label>

          {withData && !results && (
            <div className={styles.hint}><Icon name="warn" size={11} /> Grosses collections : la copie peut être longue.</div>
          )}
          {sameTarget && !results && (
            <div className={styles.hintDanger}><Icon name="warn" size={11} /> Cible identique à la source : tout sera « déjà présent » (sauté).</div>
          )}

          {job.mode === 'collections' && !results && (
            <div className={styles.collChips}>
              {job.colls?.map((c) => <span key={c} className={styles.chip}>{c}</span>)}
            </div>
          )}

          {error && <div className={styles.err}><Icon name="warn" size={12} /> {error}</div>}

          {results && counts && (
            <div className={styles.results}>
              <div className={styles.resultsSummary}>
                <Badge tone="success">{counts.copied} copiée(s)</Badge>
                {counts.skipped > 0 && <Badge tone="muted">{counts.skipped} sautée(s)</Badge>}
                {counts.missing > 0 && <Badge tone="warning">{counts.missing} absente(s)</Badge>}
                {counts.error > 0 && <Badge tone="danger">{counts.error} erreur(s)</Badge>}
              </div>
              <div className={styles.resultsList}>
                {results.length === 0 && <div className={styles.mutedSm}>Rien à copier.</div>}
                {results.map((r, i) => (
                  <div key={`${r.db ?? ''}.${r.collection}.${i}`} className={styles.resultRow}>
                    <span className={styles.resultDot} data-status={r.status} />
                    <code>{r.db ? `${r.db} · ` : ''}{r.collection}</code>
                    <span style={{ flex: 1 }} />
                    {r.status === 'copied' ? (
                      <span className={styles.mutedSm}>{r.indexes ?? 0} idx{typeof r.documents === 'number' ? ` · ${r.documents} docs` : ''}</span>
                    ) : (
                      <span className={styles.mutedSm}>{r.reason ?? r.status}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.modalFoot}>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={running}>{results ? 'Fermer' : 'Annuler'}</Button>
          {!results ? (
            <Button variant="primary" size="sm" loading={running} onClick={() => void run()}>Lancer la copie</Button>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => { setResults(null); setError(null); }}>Nouvelle copie</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function DocCard({
  doc, readOnly, onSaved, onDeleted, projectId, env, db, coll,
}: {
  doc: Record<string, unknown>;
  readOnly: boolean;
  onSaved: () => void;
  onDeleted: () => void;
  projectId: string; env: string; db: string; coll: string;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const hasId = doc._id != null;

  const beginEdit = () => { setText(JSON.stringify(doc, null, 2)); setEditing(true); };

  const save = async () => {
    let parsed: Record<string, unknown>;
    try { parsed = JSON.parse(text); }
    catch { alert('Document JSON invalide.'); return; }
    // $set des champs présents (hors _id) + $unset des champs RETIRÉS par l'utilisateur
    // (sinon un champ supprimé dans l'éditeur resterait en base et réapparaîtrait).
    const set: Record<string, unknown> = { ...parsed };
    delete set._id;
    const unset: Record<string, ''> = {};
    for (const k of Object.keys(doc)) {
      if (k !== '_id' && !(k in parsed)) unset[k] = '';
    }
    const update: Record<string, unknown> = {};
    if (Object.keys(set).length) update.$set = set;
    if (Object.keys(unset).length) update.$unset = unset;
    if (Object.keys(update).length === 0) { setEditing(false); return; } // aucun changement
    setBusy(true);
    try {
      await mongoService.updateOne(projectId, env, db, coll, { _id: doc._id }, update);
      setEditing(false);
      onSaved();
    } catch (e) { alert('Update KO : ' + errMsg(e)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!confirm('Supprimer ce document ?')) return;
    setBusy(true);
    try {
      await mongoService.deleteOne(projectId, env, db, coll, { _id: doc._id });
      onDeleted();
    } catch (e) { alert('Delete KO : ' + errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className={styles.docCard}>
      {editing ? (
        <>
          <textarea className={styles.docEditor} value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} rows={Math.min(20, text.split('\n').length + 1)} />
          <div className={styles.docActions}>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Annuler</Button>
            <Button variant="primary" size="sm" loading={busy} onClick={() => void save()}>Enregistrer</Button>
          </div>
        </>
      ) : (
        <>
          <pre className={styles.docJson}>{JSON.stringify(doc, null, 2)}</pre>
          {!readOnly && (
            <div className={styles.docActions}>
              <Button variant="ghost" size="sm" leftIcon={<Icon name="settings" size={11} />} disabled={!hasId} onClick={beginEdit} title={hasId ? undefined : 'Document sans _id : édition indisponible'}>Éditer</Button>
              <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={11} />} disabled={!hasId || busy} onClick={() => void del()} title={hasId ? undefined : 'Document sans _id : suppression indisponible'}>Supprimer</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
